import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { stringingApi } from '../api';
import { StringingOrder, StringingOrderCreate } from '../api_types';
import { format } from 'date-fns';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  XCircleIcon,
  CreditCardIcon,
  BanknotesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { auth } from '../firebase';

interface StringingServiceProps {
  businessId: string;
  facilityId: string;
}

export const StringingService: React.FC<StringingServiceProps> = ({ businessId, facilityId }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<StringingOrder[]>([]);
  const [latestOrder, setLatestOrder] = useState<StringingOrder | null>(null);
  const [savedOrder, setSavedOrder] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewOrderForm, setShowNewOrderForm] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [newOrder, setNewOrder] = useState<Partial<StringingOrderCreate>>({
    facilityId,
    racquetDetails: {
      brand: '',
      model: '',
      stringPattern: '',
      tension: { mains: 0, crosses: 0 }
    },
    stringDetails: {
      mains: '',
      crosses: ''
    },
    customerDetails: {
      name: '',
      email: '',
      phone: ''
    },
    notes: '',
    paymentMethod: 'card'
  });

  // Fetch saved stringing order
  useEffect(() => {
    const fetchSavedOrder = async () => {
      if (!user) return;
      
      try {
        const response = await stringingApi.getSavedOrder(businessId, facilityId, user.uid);
        if (response.savedOrder) {
          setSavedOrder(response.savedOrder);
        }
      } catch (err) {
        console.error('Error fetching saved stringing order:', err);
      }
    };

    fetchSavedOrder();
  }, [user, facilityId, businessId]);

  // Fetch all orders
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const ordersData = await stringingApi.getOrders(businessId, facilityId);
        setOrders(ordersData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch orders';
        setError(errorMessage);
        console.error('Error fetching orders:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [facilityId, businessId]);

  // Add useEffect to handle URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const status = searchParams.get('status');
    const orderId = searchParams.get('order_id');
    
    if (status && orderId) {
      if (status === 'success') {
        // Check payment status with Stripe
        const checkPaymentStatus = async () => {
          try {
            // Get the session ID from the order
            const order = await stringingApi.getOrder(businessId, facilityId, orderId);
            if (order.stripeSessionId) {
              const paymentStatus = await stringingApi.getPaymentStatus(order.id, order.stripeSessionId);
              if (paymentStatus.paymentStatus === 'paid') {
                setNotification({ type: 'success', message: 'Payment successful! Your order has been confirmed.' });
                // Refresh orders to show updated status
                const ordersData = await stringingApi.getOrders(businessId, facilityId);
                setOrders(ordersData);
              } else {
                setNotification({ type: 'error', message: 'Payment is still processing. Please wait a moment and refresh the page.' });
              }
            } else {
              setNotification({ type: 'error', message: 'Error: No payment session found for this order.' });
            }
          } catch (err) {
            console.error('Error checking payment status:', err);
            setNotification({ type: 'error', message: 'Error verifying payment status. Please contact support.' });
          }
        };
        checkPaymentStatus();
      } else if (status === 'cancel') {
        setNotification({ type: 'error', message: 'Payment was cancelled. Please try again.' });
      }
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [facilityId, businessId]);

  const handleCreateOrder = async () => {
    try {
      if (!user) {
        setError('Please log in to create an order');
        return;
      }

      setIsProcessingPayment(true);
      setError(null);

      const order = await stringingApi.createOrder({
        ...newOrder as StringingOrderCreate,
        businessId,
        facilityId
      });
      setOrders([...orders, order]);
      setShowNewOrderForm(false);
      
      // If payment method is card, create checkout session
      if (newOrder.paymentMethod === 'card') {
        try {
          // Get the current user's token
          const token = await auth.currentUser?.getIdToken();
          if (!token) {
            throw new Error('Authentication token not available');
          }

          const checkoutResponse = await stringingApi.createCheckout(businessId, facilityId, order.id, {
            memberId: user.uid,
            useCredits: false,
            successUrl: `${window.location.origin}/member/dashboard/stringing?status=success&order_id=${order.id}`,
            cancelUrl: `${window.location.origin}/member/dashboard/stringing?status=cancel&order_id=${order.id}`
          });

          if (!checkoutResponse.url) {
            throw new Error('No checkout URL received from server');
          }
          window.location.href = checkoutResponse.url;
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
          setError(errorMessage);
          console.error('Error creating checkout session:', err);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create order';
      setError(errorMessage);
      console.error('Error creating order:', err);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleUseSavedOrder = () => {
    if (!savedOrder) return;

    setNewOrder({
      facilityId,
      racquetDetails: {
        brand: savedOrder.racquetDetails.brand || '',
        model: savedOrder.racquetDetails.model || '',
        stringPattern: savedOrder.racquetDetails.stringPattern || '',
        tension: {
          mains: savedOrder.racquetDetails.tension?.mains || 0,
          crosses: savedOrder.racquetDetails.tension?.crosses || 0
        }
      },
      stringDetails: {
        mains: savedOrder.stringDetails.mains || '',
        crosses: savedOrder.stringDetails.crosses || ''
      },
      customerDetails: {
        name: savedOrder.customerDetails?.name || '',
        email: savedOrder.customerDetails?.email || '',
        phone: savedOrder.customerDetails?.phone || ''
      },
      notes: '',
      paymentMethod: 'card'
    });
    setShowNewOrderForm(true);
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>;
  }

  if (error) {
    return <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <XCircleIcon className="h-5 w-5 text-red-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      </div>
    </div>;
  }

  return (
    <div className="space-y-6">
      {notification && (
        <div className={`p-4 rounded-md ${
          notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setNotification(null)}
                className="inline-flex text-gray-400 hover:text-gray-500"
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Stringing Service</h2>
        <div className="flex space-x-3">
          {latestOrder && (
            <button
              onClick={() => {
                setNewOrder({
                  facilityId,
                  racquetDetails: latestOrder.racquetDetails,
                  stringDetails: latestOrder.stringDetails,
                  customerDetails: latestOrder.customerDetails,
                  notes: '',
                  paymentMethod: 'card'
                });
                setShowNewOrderForm(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Use Previous Settings
            </button>
          )}
          <button
            onClick={() => setShowNewOrderForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            New Order
          </button>
        </div>
      </div>

      {/* Orders List */}
      {orders.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {orders.map((order) => (
              <li key={order.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-indigo-600 truncate">
                        {order.racquetDetails.brand} {order.racquetDetails.model}
                      </p>
                      <div className="ml-2 flex-shrink-0 flex">
                        {order.status === 'completed' && (
                          <CheckCircleIcon className="h-5 w-5 text-green-400" />
                        )}
                        {order.status === 'pending' && (
                          <ClockIcon className="h-5 w-5 text-yellow-400" />
                        )}
                        {order.status === 'cancelled' && (
                          <XCircleIcon className="h-5 w-5 text-red-400" />
                        )}
                      </div>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex">
                      <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        ${order.price}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <p className="flex items-center text-sm text-gray-500">
                        {order.stringDetails.mains} / {order.stringDetails.crosses}
                      </p>
                      <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                        {order.racquetDetails.tension.mains} / {order.racquetDetails.tension.crosses} lbs
                      </p>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                      <p>
                        {format(new Date(order.createdAt), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="bg-white shadow sm:rounded-md p-6 text-center">
          <p className="text-gray-500">No stringing orders yet. Click "New Order" to get started.</p>
        </div>
      )}

      {/* New Order Form */}
      {showNewOrderForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {latestOrder ? 'New Stringing Order' : 'Your First Stringing Order'}
              </h3>
              {savedOrder ? (
                <button
                  onClick={handleUseSavedOrder}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ArrowPathIcon className="h-5 w-5 mr-1.5" />
                  Use Saved Settings
                </button>
              ) : (
                <div className="text-sm text-gray-500 italic">
                  Complete your first order to save your preferences
                </div>
              )}
            </div>
            
            {!latestOrder && (
              <div className="mb-4 p-4 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-700">
                  Welcome to our stringing service! Please fill out the form below with your racquet details.
                </p>
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); handleCreateOrder(); }}>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Racquet Details</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Brand</label>
                      <input
                        type="text"
                        value={newOrder.racquetDetails?.brand}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          racquetDetails: {
                            ...newOrder.racquetDetails!,
                            brand: e.target.value
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Model</label>
                      <input
                        type="text"
                        value={newOrder.racquetDetails?.model}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          racquetDetails: {
                            ...newOrder.racquetDetails!,
                            model: e.target.value
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">String Pattern</label>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500">Mains</label>
                          <input
                            type="number"
                            min="12"
                            max="20"
                            value={newOrder.racquetDetails?.stringPattern?.split(' x ')[0] || ''}
                            onChange={(e) => {
                              const mains = e.target.value;
                              const crosses = newOrder.racquetDetails?.stringPattern?.split(' x ')[1] || '';
                              setNewOrder({
                                ...newOrder,
                                racquetDetails: {
                                  ...newOrder.racquetDetails!,
                                  stringPattern: `${mains} x ${crosses}`
                                }
                              });
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500">Crosses</label>
                          <input
                            type="number"
                            min="12"
                            max="20"
                            value={newOrder.racquetDetails?.stringPattern?.split(' x ')[1] || ''}
                            onChange={(e) => {
                              const crosses = e.target.value;
                              const mains = newOrder.racquetDetails?.stringPattern?.split(' x ')[0] || '';
                              setNewOrder({
                                ...newOrder,
                                racquetDetails: {
                                  ...newOrder.racquetDetails!,
                                  stringPattern: `${mains} x ${crosses}`
                                }
                              });
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Mains Tension</label>
                        <input
                          type="number"
                          value={newOrder.racquetDetails?.tension.mains}
                          onChange={(e) => setNewOrder({
                            ...newOrder,
                            racquetDetails: {
                              ...newOrder.racquetDetails!,
                              tension: {
                                ...newOrder.racquetDetails!.tension,
                                mains: parseInt(e.target.value)
                              }
                            }
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Crosses Tension</label>
                        <input
                          type="number"
                          value={newOrder.racquetDetails?.tension.crosses}
                          onChange={(e) => setNewOrder({
                            ...newOrder,
                            racquetDetails: {
                              ...newOrder.racquetDetails!,
                              tension: {
                                ...newOrder.racquetDetails!.tension,
                                crosses: parseInt(e.target.value)
                              }
                            }
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700">String Details</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Mains</label>
                      <input
                        type="text"
                        value={newOrder.stringDetails?.mains}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          stringDetails: {
                            ...newOrder.stringDetails!,
                            mains: e.target.value
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Crosses</label>
                      <input
                        type="text"
                        value={newOrder.stringDetails?.crosses}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          stringDetails: {
                            ...newOrder.stringDetails!,
                            crosses: e.target.value
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700">Customer Details</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <input
                        type="text"
                        value={newOrder.customerDetails?.name}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          customerDetails: {
                            ...newOrder.customerDetails!,
                            name: e.target.value
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        value={newOrder.customerDetails?.email}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          customerDetails: {
                            ...newOrder.customerDetails!,
                            email: e.target.value
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <input
                        type="tel"
                        value={newOrder.customerDetails?.phone}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          customerDetails: {
                            ...newOrder.customerDetails!,
                            phone: e.target.value
                          }
                        })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700">Payment Method</h4>
                  <div className="mt-2 flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="card"
                        checked={newOrder.paymentMethod === 'card'}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          paymentMethod: e.target.value as 'card' | 'cash'
                        })}
                        className="form-radio h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2 flex items-center">
                        <CreditCardIcon className="h-5 w-5 text-gray-400 mr-1" />
                        Card
                      </span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="cash"
                        checked={newOrder.paymentMethod === 'cash'}
                        onChange={(e) => setNewOrder({
                          ...newOrder,
                          paymentMethod: e.target.value as 'card' | 'cash'
                        })}
                        className="form-radio h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2 flex items-center">
                        <BanknotesIcon className="h-5 w-5 text-gray-400 mr-1" />
                        Cash
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={newOrder.notes}
                    onChange={(e) => setNewOrder({
                      ...newOrder,
                      notes: e.target.value
                    })}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowNewOrderForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Create Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StringingService; 